'use client';

import type { SRPFilterAction, SRPState } from '@dealerportal/common';
import {
  getFilterTranslationKey,
  translateOrFallback,
  translateUnitSuffix,
  useTranslations,
} from '@dealerportal/i18n/client';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import classNames from 'classnames';
import { type Dispatch, type FC, useEffect, useRef, useState } from 'react';
import slugify from 'slugify';
import { FilterPills } from './FilterPills';

interface FilterMenuProps {
  filters: SRPState['filters'];
  needsHydration: boolean;
  isMobileDevice: boolean;
  closeMenu: () => void;
  dispatch: Dispatch<SRPFilterAction>;
}

export const FilterMenu: FC<FilterMenuProps> = (props: FilterMenuProps) => {
  const namespace = 'specs';
  const t = useTranslations(namespace);
  const dispatch = props.dispatch;

  const translateFilterName = (filterName: string): string =>
    translateOrFallback(
      t,
      namespace,
      getFilterTranslationKey(filterName),
      filterName
    );

  const reorderedFilters = (() => {
    const filters = [...props.filters];

    // Handle 'Fiber Brand'
    const fiberBrandIndex = filters.findIndex((f) => f.name === 'Fiber Brand');
    const lineIndex = filters.findIndex((f) => f.name === 'Line');
    const brandIndex = filters.findIndex((f) => f.name === 'Brand');

    if (fiberBrandIndex > -1) {
      const [fiberBrandFilter] = filters.splice(fiberBrandIndex, 1);
      if (lineIndex > -1) {
        filters.splice(lineIndex + 1, 0, fiberBrandFilter);
      } else if (brandIndex > -1) {
        filters.splice(brandIndex + 1, 0, fiberBrandFilter);
      }
    }

    // Handle 'Fiber Type' (Move to the bottom)
    const fiberTypeIndex = filters.findIndex((f) => f.name === 'Fiber Type');
    if (fiberTypeIndex > -1) {
      const [fiberTypeFilter] = filters.splice(fiberTypeIndex, 1);
      filters.push(fiberTypeFilter);
    }

    return filters;
  })();

  // Calculate the count of applied filters
  const appliedFilterCount = props.filters.reduce(
    (acc, filter) => acc + filter.enabledValues.length,
    0
  );

  // Edge case to hide Brand filters with 0 counts on first load of a product type srp page (e.g. /p/hardwood/)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      // Do something on first render...
      const anyOtherFilterIsEnabled =
        props.filters.find(
          (f) => f.name !== 'Brand' && f.enabledValues.length > 0
        ) != null;
      if (anyOtherFilterIsEnabled) {
        const brandFilter = props.filters.find((f) => f.name === 'Brand');
        if (brandFilter != null) {
          for (const [key, value] of Object.entries(
            brandFilter.potentialValues
          )) {
            if (value === 0) {
              brandFilter.potentialValues[key] = -1;
            }
          }
        }
      }
    }
    isFirstRender.current = false;

    // ---> StrictMode: The following is REQUIRED to reset/cleanup:
    return () => {
      isFirstRender.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // End of edge case

  // Render filters using the reordered array
  const filtersRendered = reorderedFilters.map((filter, i) =>
    ['Samples', 'Visualizable', 'Favorites', 'Tags'].includes(filter.name) ? (
      ''
    ) : (
      <FilterMenuSection
        filter={{ ...filter, hideFilterHeading: true }}
        needsHydration={props.needsHydration}
        title={translateFilterName(filter.name)}
        dispatch={dispatch}
        key={filter.name}
        initiallyExpanded={
          filter.name === 'Brand' || (filter.enabledValues?.length ?? 0) > 0
        }
        last={reorderedFilters.length - 1 === i}
      />
    )
  );
  const samplesFilter = props.filters.find(
    (filter) => filter.name === 'Samples'
  );
  const VisualizableFilter = props.filters.find(
    (filter) => filter.name === 'Visualizable'
  );
  const FavoritesFilter = props.filters.find(
    (filter) => filter.name === 'Favorites'
  );
  const tagsFilter = props.filters.find((filter) => filter.name === 'Tags');
  const hasBooleanFilters =
    (samplesFilter && samplesFilter.potentialValues['true'] !== 0) ||
    (VisualizableFilter && VisualizableFilter.potentialValues['true'] !== 0) ||
    (FavoritesFilter && FavoritesFilter.potentialValues['true'] !== 0);

  return (
    <div className="search-filters ui-h-full ui-flex ui-flex-col ui-justify-between">
      {/* Header */}
      <div className="ui-flex ui-flex-col ui-py-4 ui-px-6 ui-bg-gray-100 md:ui-bg-transparent ui-border-solid ui-border-0 ui-border-b ui-border-gray-300 md:ui-border-gray-200">
        <div className="ui-flex ui-justify-between ui-mb-[-15px] md:ui-mb-1">
          <div className="ui-text-lg ui-font-semibold">
            {t('filters')} ({appliedFilterCount})
          </div>
          {appliedFilterCount > 0 && !props.isMobileDevice && (
            <BetterButton
              label={t('clearAll')}
              labelClassName="ui-text-sm"
              btnStyle="simple"
              className="hover:ui-text-blue-800 ui-font-semibold ui-ml-auto"
              replaceClass={{
                '!ui-text-gray-950': '!ui-text-blue-600',
              }}
              attributes={{
                onClick: () => {
                  dispatch({ type: 'CLEAR_ALL' });
                },
              }}
            />
          )}
          {/* Only show close button on mobile */}
          <div>
            {props.isMobileDevice && (
              <button
                data-testid="close-button"
                onClick={() => props.closeMenu()}
                className="search-filters__toggle ui-border-0 ui-bg-transparent"
              >
                <svg
                  className="ui-w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                >
                  <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div>
          {appliedFilterCount > 0 && props.isMobileDevice && (
            <button
              onClick={() => dispatch({ type: 'CLEAR_ALL' })}
              className="ui-text-blue-600 !ui-text-[14px] !ui-leading-5 ui-font-medium ui-border-none ui-border-transparent ui-rounded hover:ui-text-blue-800 ui-underline ui-ml-auto"
            >
              {t('clearAll')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        className={classNames(
          'search-filters__filters ui-overflow-y-auto ui-flex-1 ui-px-6 ui-mb-px ui-space-y-4',
          { 'ui-py-5': appliedFilterCount > 0 }
        )}
      >
        <div
          className={classNames({
            'ui-flex-col ui-pb-3 ui-border-solid ui-border-0 ui-border-b ui-border-gray-300 md:ui-border-gray-200':
              appliedFilterCount > 0,
          })}
        >
          <FilterPills filters={props.filters} dispatch={dispatch} />
        </div>
        {hasBooleanFilters && (
          <div className="ui-pb-4 ui-space-y-4 ui-border-solid ui-border-0 ui-border-b ui-border-gray-300">
            <FilterMenuToggle
              title={t('samples')}
              filterKey="Samples"
              key="Samples"
              dispatch={dispatch}
              potentialValues={samplesFilter?.potentialValues ?? {}}
              initialChecked={samplesFilter?.enabledValues?.indexOf('1') !== -1}
            />
            <FilterMenuToggle
              title={t('visualizable')}
              filterKey="Visualizable"
              key="Visualizable"
              dispatch={dispatch}
              potentialValues={VisualizableFilter?.potentialValues ?? {}}
              initialChecked={
                VisualizableFilter?.enabledValues?.indexOf('1') !== -1
              }
            />
            <FilterMenuToggle
              title={t('favorites')}
              filterKey="Favorites"
              key="Favorites"
              dispatch={dispatch}
              potentialValues={FavoritesFilter?.potentialValues ?? {}}
              initialChecked={
                FavoritesFilter?.enabledValues?.indexOf('1') !== -1
              }
            />
          </div>
        )}

        {filtersRendered}

        <div
          className={classNames({
            'ui-space-y-4 ui-pb-4 ui-border-solid ui-border-0 ui-border-b ui-border-gray-300':
              Object.keys(tagsFilter?.potentialValues || {}).length,
          })}
        >
          {tagsFilter && <TagsFilter filter={tagsFilter} dispatch={dispatch} />}
        </div>
      </div>

      {/* Footer */}
      <div className="ui-flex ui-gap-3 ui-p-4 ui-border-solid ui-border-0 ui-border-t ui-bg-gray-100 md:ui-bg-transparent ui-border-gray-300 md:ui-border-gray-200">
        <BetterButton
          label={t('clearCount', { count: appliedFilterCount })}
          btnStyle="neutral"
          className={'search-filters__clear-btn'}
          labelClassName="ui-text-sm"
          replaceClass={{
            '!ui-px-6': '!ui-px-4',
          }}
          attributes={{
            onClick: () => {
              dispatch({ type: 'CLEAR_ALL' });
            },
          }}
        />

        <BetterButton
          label={t('apply')}
          btnStyle="primary"
          className="search-filters__apply-btn ui-flex-1"
          labelClassName="ui-text-sm"
          attributes={{
            onClick: () => {
              dispatch({ type: 'APPLY' });
            },
          }}
        />
      </div>
    </div>
  );
};

interface FilterMenuSectionProps {
  title: string;
  filter: SRPState['filters'][0];
  needsHydration: boolean;
  dispatch: Dispatch<SRPFilterAction>;
  initiallyExpanded: boolean;
  last: boolean;
}

export const FilterMenuSection: FC<FilterMenuSectionProps> = (
  props: FilterMenuSectionProps
) => {
  const t = useTranslations('specs');
  // State for managing the expanded/collapsed state of the section
  const [expanded, setExpanded] = useState(props.initiallyExpanded);
  const disabled = Object.keys(props.filter.potentialValues).every(
    (k) => props.filter.potentialValues[k] === 0
  );
  useEffect(() => {
    setExpanded(!disabled && expanded);

    if (props.title === 'Type')
      setExpanded(props.filter.enabledValues.length === 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // Determine button icon based on expanded state
  const buttonPath = expanded
    ? '<path className="ui-fill-gray-700" d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z"/>'
    : '<path className="ui-fill-gray-700" d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/>';

  // Apply classNames based on expanded state
  const sectionClasses = classNames('', { 'ui-hidden': !expanded });

  return (
    <div
      className={classNames(
        'ui-pb-4 ui-border-solid ui-border-0 ui-border-b ui-border-gray-300',
        {
          'ui-border-transparent': props.last,
        }
      )}
      data-filter-title={props.title.toLowerCase()}
      data-filter-selected={props.filter.enabledValues.join(',')}
    >
      {/* Header */}
      <div>
        <button
          onClick={() => {
            if (!disabled) setExpanded(!expanded);
          }}
          className="ui-w-full ui-max-w-none ui-flex ui-items-center ui-justify-between !ui-leading-tight ui-border-0 ui-bg-transparent !ui-rounded-none focus:ui-outline-none focus:!ui-ring-0 ui-select-none"
          title={expanded ? t('hideFilterMenu') : t('showFilterMenu')}
        >
          <div
            className={classNames(
              'ui-font-bold',
              { 'ui-text-gray-400': disabled, 'ui-text-gray-700': !disabled },
              { 'ui-cursor-not-allowed': disabled }
            )}
          >
            {props.title}
          </div>

          <svg
            className={classNames('ui-w-3', { 'ui-invisible': disabled })}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            dangerouslySetInnerHTML={{ __html: buttonPath }}
          />
        </button>
      </div>

      {/* Filters */}
      <div className={sectionClasses}>
        <Filters
          {...props.filter}
          needsHydration={props.needsHydration}
          isMobileDevice={true}
          dispatch={props.dispatch}
        />
      </div>
    </div>
  );
};

type FilterMenuToggleProps = Pick<FilterMenuSectionProps, 'dispatch'> & {
  potentialValues: Record<string, number>;
  initialChecked: boolean;
  title: string;
  filterKey: 'Samples' | 'Visualizable' | 'Favorites';
};

const FilterMenuToggle: FC<FilterMenuToggleProps> = ({
  title,
  filterKey,
  dispatch,
  potentialValues,
  initialChecked,
}: FilterMenuToggleProps) => {
  const handleClick = (checked: boolean) => {
    const dispatchVal = {
      type: !checked ? 'REMOVE' : 'UPDATE',
      filter: filterKey,
      value: '1',
    } as SRPFilterAction;
    dispatch(dispatchVal);
  };
  return (
    <FilterToggle
      title={title}
      initialChecked={initialChecked}
      onToggle={handleClick}
      hidden={potentialValues['true'] === 0}
    />
  );
};

type TagsFilterProps = Pick<FilterMenuSectionProps, 'dispatch'> & {
  filter: SRPState['filters'][0];
};

const TagsFilter: FC<TagsFilterProps> = ({ filter, dispatch }) => {
  const handleClick = (displayName: string, checked: boolean) => {
    const action = {
      type: !checked ? 'REMOVE' : 'UPDATE',
      filter: 'Tags',
      value: slugify(displayName, { replacement: '_' }),
    } as SRPFilterAction;
    dispatch(action);
  };
  return Object.keys(filter.potentialValues).map((tag) => {
    const displayName = tag.replace(/_/g, ' ');
    if (displayName.toLowerCase() === 'sale') return null;
    return (
      <FilterToggle
        key={tag}
        title={displayName}
        initialChecked={filter.enabledValues?.indexOf(tag) !== -1}
        onToggle={(checked) => handleClick(displayName, checked)}
        hidden={
          filter.potentialValues[tag] === 0 || displayName === 'Order Sample'
        }
      />
    );
  });
};

interface FilterToggleProps {
  title: string;
  initialChecked: boolean;
  onToggle: (checked: boolean) => void;
  hidden: boolean;
}

const FilterToggle = ({
  title,
  initialChecked,
  onToggle,
  hidden,
}: FilterToggleProps) => {
  const [checked, setChecked] = useState(initialChecked);
  useEffect(() => {
    setChecked(initialChecked && !hidden);
  }, [initialChecked, hidden]);

  if (hidden) return null;

  return (
    <div className="ui-flex" data-filter-title={title.toLowerCase()}>
      <div className="ui-flex ui-w-full ui-justify-between">
        <span className="ui-font-bold ui-text-gray-700">{title}</span>
        <button
          className={'ui-border-none ui-flex ui-items-center'}
          onClick={() => {
            setChecked(!checked);
            onToggle(!checked);
          }}
        >
          {!checked ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ui-w-6"
              viewBox="0 0 576 512"
            >
              <path
                className="ui-fill-gray-700"
                d="M192 400a144 144 0 1 1 0-288 144 144 0 1 1 0 288zm0 48H384c106 0 192-86 192-192s-86-192-192-192H192C86 64 0 150 0 256S86 448 192 448zm127-48c39.9-35.2 65-86.7 65-144s-25.1-108.8-65-144h65c79.5 0 144 64.5 144 144s-64.5 144-144 144H319z"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ui-w-6"
              viewBox="0 0 576 512"
            >
              <path
                className="ui-fill-[var(--cta-color,#030712)]"
                d="M192 64C86 64 0 150 0 256S86 448 192 448H384c106 0 192-86 192-192s-86-192-192-192H192zm192 48a144 144 0 1 1 0 288 144 144 0 1 1 0-288z"
              />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

interface FiltersProps {
  needsHydration: boolean;
  name: string;
  enabledValues?: string[];
  potentialValues: Record<string, number>;
  displayNames?: Record<string, string>;
  displayNameKeys?: Record<string, string>;
  /** English unit suffix present on every `displayNames` entry, if any. */
  uom?: string;
  hideFilterHeading?: boolean;
  dispatch: Dispatch<SRPFilterAction>;
  isMobileDevice: boolean;
}

export const Filters: FC<FiltersProps> = ({
  name,
  enabledValues,
  potentialValues,
  displayNames,
  displayNameKeys,
  uom,
  hideFilterHeading,
  needsHydration,
  dispatch,
}: FiltersProps) => {
  const namespace = 'specs';
  const t = useTranslations(namespace);

  const displayValueFor = (value: string): string => {
    const fallback = displayNames?.[value] ?? value;
    const messageKey = displayNameKeys?.[value];
    const label = messageKey
      ? translateOrFallback(t, namespace, messageKey, fallback)
      : fallback;
    return translateUnitSuffix(t, namespace, label, uom);
  };

  const filterCount = Object.keys(potentialValues).length;

  const MAX_FILTERS_SHOWN = 5;

  const [expanded, setExpanded] = useState<boolean>(false);

  // Sort filters by their color count being 0 or not, then sort alphabetically
  const sortedValues = Object.keys(potentialValues)
    .filter(
      (key) =>
        key !== 'Samples' && key !== 'Visualizable' && key !== 'Favorites'
    )
    .sort((a, b) => {
      const enabledA = potentialValues[a] > 0 ? 1 : 0;
      const enabledB = potentialValues[b] > 0 ? 1 : 0;
      return enabledB - enabledA || a.localeCompare(b);
    });
  return (
    <div data-testid="filters">
      <div className="ui-mt-4 ui-overflow-hidden">
        {/* Filter heading */}
        {!hideFilterHeading && (
          <div
            className="ui-mb-3 ui-font-normal ui-pb-1 ui-mt-0.5 ui-text-base ui-inline-flex"
            style={{
              borderBottom: '2px solid',
            }}
          >
            {name}
          </div>
        )}

        {/* Render filter items, sorted with checked values at the top */}
        {sortedValues
          .slice(0, !expanded ? MAX_FILTERS_SHOWN : filterCount)
          .map((value: string) => (
            <Filter
              needsHydration={needsHydration}
              dispatch={dispatch}
              name={name}
              displayValue={displayValueFor(value)}
              value={value}
              checked={
                enabledValues?.some(
                  (enabledValue) =>
                    enabledValue.toLowerCase() === value.toLowerCase()
                ) ?? false
              }
              count={potentialValues[value]}
              key={value}
            />
          ))}
      </div>

      {filterCount > MAX_FILTERS_SHOWN && (
        <BetterButton
          icon={
            expanded
              ? '<svg class="ui-w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path class="ui-fill-blue-600" d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z"/></svg>'
              : '<svg class="ui-w-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path class="ui-fill-blue-600" d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/></svg>'
          }
          label={expanded ? t('showLess') : t('showMore')}
          labelClassName="!ui-text-sm"
          btnStyle="simple"
          className="ui-mt-2 ui-font-semibold"
          replaceClass={{
            '!ui-text-gray-950': '!ui-text-blue-600',
            'ui-gap-2': 'ui-gap-1.5',
          }}
          attributes={{
            onClick: () => {
              setExpanded(!expanded);
            },
          }}
        />
      )}
    </div>
  );
};

interface FilterProps {
  needsHydration: boolean;
  count: number;
  dispatch: Dispatch<SRPFilterAction>;
  name: string;
  value: string;
  displayValue?: string;
  checked: boolean;
}

export const Filter: FC<FilterProps> = ({
  count,
  dispatch,
  name,
  value,
  displayValue,
  checked,
  needsHydration,
}: FilterProps) => {
  const t = useTranslations('specs');
  // State for managing the enabled/disabled state of the filter
  const [enabled, setEnabled] = useState(checked);

  const labelClasses = classNames(
    'ui-flex',
    'ui-items-center',
    'ui-gap-0.5',
    'ui-py-1.5',
    'ui-text-sm',
    { 'ui-text-gray-400': count === 0 },
    { 'ui-cursor-not-allowed': count === 0 }
  );
  const spinner = (
    <svg
      className="ui-text-gray-500 ui-fill-current ui-text-sm ui-animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      height="1em"
      viewBox="0 0 512 512"
    >
      <path d="M215.1 26.3c3.6 12.7-3.7 26-16.5 29.7C111.6 80.9 48 161.1 48 256c0 114.9 93.1 208 208 208s208-93.1 208-208c0-94.9-63.6-175.1-150.6-200c-12.7-3.6-20.1-16.9-16.5-29.7s16.9-20.1 29.7-16.5C433.6 40.5 512 139.1 512 256c0 141.4-114.6 256-256 256S0 397.4 0 256C0 139.1 78.4 40.5 185.4 9.9c12.7-3.6 26 3.7 29.7 16.5z" />
    </svg>
  );
  const disabledTooltip = t('incompatibleFilter');
  // Update the enabled state when the checked prop changes
  useEffect(() => {
    setEnabled(checked);
  }, [checked]);

  // Hide filter if the count is set to a special sentinal value (any negative number)
  const shouldRenderFilter = count >= 0;
  if (!shouldRenderFilter) return null;
  return (
    <div className="ui-flex ui-items-center ui-gap-4">
      <div className="ui-flex-1">
        <label
          title={count === 0 ? disabledTooltip : ''}
          htmlFor={value}
          className={labelClasses}
        >
          {/* Filter checkbox */}
          <input
            onChange={({ target: { checked } }) => {
              setEnabled(checked);
              dispatch({
                type: checked ? 'UPDATE' : 'REMOVE',
                filter: name,
                value,
              });
            }}
            type="checkbox"
            id={value}
            checked={enabled}
            disabled={count === 0 || needsHydration}
            value={value}
            className="!ui-w-5 !ui-h-5 ui-shrink-0 disabled:ui-accent-gray-400 ui-opacity-1 ui-mr-[10px]"
          />
          {/* Filter label */}

          <span data-code={value}>{displayValue ?? value}</span>
        </label>
      </div>

      {/* Filter count */}
      {needsHydration && spinner}
      {!needsHydration && (
        <label className="ui-text-xs ui-text-gray-500">({count})</label>
      )}
    </div>
  );
};
